import React from 'react';

import { Box, Fade, Paper, Typography, useTheme } from '@material-ui/core';

import { TextListDataPoint } from '@vyce/core/src/types';
import { FormSummaryQuestionTitle } from './FormSummaryQuestionTitle';
import { ReadMore } from '@vyce/core/src/components/ReadMore';

const getAnswerText = (answer: TextListDataPoint): string => {
  if (typeof answer.value === 'string') return answer.value;
  return (answer.value as { text?: string })?.text ?? '';
};

interface Props {
  title: string;
  answers: TextListDataPoint[];
  totalAnswers: number;
  description?: string;
  videoLink?: string;
  photoUrl?: string;
  explanation?: string;
  pdfUrl?: string;
}

export const FormSummaryText: React.FC<Props> = ({
  title,
  answers,
  totalAnswers,
  description,
  videoLink,
  photoUrl,
  explanation,
  pdfUrl,
}) => {
  const theme = useTheme();

  const answerRows = (variant: 'screen' | 'print') =>
    answers.map((answer, index) => (
      <Box
        key={answer.index}
        display="flex"
        paddingBottom={2}
        paddingTop={2}
        style={{
          borderBottom: index < answers.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
        }}
      >
        <Box flex={4}>
          {variant === 'screen' ? (
            <ReadMore lines={3}>{getAnswerText(answer)}</ReadMore>
          ) : (
            <Typography
              variant="body1"
              component="div"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {getAnswerText(answer)}
            </Typography>
          )}
        </Box>
        <Box flex={1}>
          <Typography variant="body2" color="textSecondary">
            {answer.participantName}
          </Typography>
        </Box>
      </Box>
    ));

  return (
    <Fade in={true} timeout={200} style={{ width: '100%' }}>
      <Paper variant="outlined" style={{ padding: 24, width: '100%' }}>
        <FormSummaryQuestionTitle
          title={title}
          totalAnswers={totalAnswers}
          description={description}
          videoLink={videoLink}
          photoUrl={photoUrl}
          explanation={explanation}
          pdfUrl={pdfUrl}
        />

        <Box
          className="form-summary-text-answers form-summary-screen-only"
          marginTop={2}
          style={{ overflowY: 'auto', maxHeight: 400 }}
        >
          {answerRows('screen')}
        </Box>

        <Box className="form-summary-text-answers form-summary-print-only" marginTop={2}>
          {answerRows('print')}
        </Box>
      </Paper>
    </Fade>
  );
};
